import React from 'react';

import styles from './processFlow.module.scss';

import { faClass } from '@/constants/icons';

/**
 * Stage path of a process — the Salesforce-style chevron strip.
 *
 * Purely presentational and definition-driven: it renders whatever `stages` it
 * is handed and knows nothing about any particular workflow.
 *
 * Semantics: a `<nav>` wrapping an ordered list, current stage marked with
 * `aria-current="step"`, and each step's state ("completata", "corrente", "da
 * fare") exposed as visually-hidden text so it is not conveyed by colour and
 * shape alone.
 *
 * @param {Array<{key, label, icon, variant, is_terminal, is_success}>} props.stages
 * @param {string} props.currentKey
 * @param {(key: string) => void} [props.onSelect] omit for a read-only path
 * @param {string} [props.ariaLabel]
 */
export default function ProcessPath({ stages = [], currentKey, onSelect, ariaLabel }) {
  const currentIndex = stages.findIndex((s) => s.key === currentKey);

  const stateOf = (index, stage) => {
    if (index === currentIndex) return 'current';
    // Terminal stages are alternative endings, not steps on the way: they never
    // read as "already done" just because the pipeline moved past them.
    if (stage.is_terminal) return 'upcoming';
    return index < currentIndex ? 'done' : 'upcoming';
  };

  return (
    <nav className={styles.path} aria-label={ariaLabel || 'Stato pratica'}>
      <ol className={styles.pathList}>
        {stages.map((stage, index) => {
          const state = stateOf(index, stage);
          const isCurrent = state === 'current';
          const Tag = onSelect ? 'button' : 'div';

          return (
            <li
              key={stage.key}
              className={styles.pathItem}
              data-state={state}
              data-variant={stage.variant || 'info'}
              aria-current={isCurrent ? 'step' : undefined}
            >
              <Tag
                type={onSelect ? 'button' : undefined}
                className={styles.pathStep}
                onClick={onSelect ? () => onSelect(stage.key) : undefined}
                title={stage.label}
              >
                {stage.icon && (
                  <i className={faClass(stage.icon, styles.pathIcon)} aria-hidden="true" />
                )}
                <span className={styles.pathLabel}>{stage.label}</span>
                <span className={styles.srOnly}>
                  {state === 'done' ? ' (completata)' : isCurrent ? ' (corrente)' : ' (da fare)'}
                </span>
              </Tag>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
